/**
 * @file    enterpriseReducer.ts
 * @scope   STATE
 * @summary The single source of truth for DIY Enterprise V2 state. Pure reducer with one `case`
 *          per `EnterpriseAction` (partitions, fields, view options, sync, strip, reset).
 * @consumes EnterpriseGlobalModel mutators (models/Enterprise), field/filter validation strippers
 * @produces `enterpriseReducer`, `EnterpriseAction`, `ENTERPRISE_INITIAL_STATE`
 * @sideEffects None — pure. Returns a NEW state on change, the SAME reference when nothing changed
 *              (so consumers can skip re-renders / effects).
 * @notes WHY strip-incomplete runs here (not in callers): it must compute against the freshest
 *        state. A read-compute-write from a component can be clobbered by an interleaved async
 *        writer — which is what made stripped fields resurrect on tab-pop. Components must go
 *        through EnterpriseContext actions; never mutate state directly.
 */
import {
  EnterpriseGlobalModel,
  updateSelectedPartitions,
  updateSelectedEnterpriseFields,
  updateAvailableFieldsManagerOptions,
  updateFieldInPartition,
} from 'models/Enterprise/EnterpriseGlobalModel';
import { PartitionModel } from 'models/Enterprise/DataSourceModel';
import {
  FieldsManagerViewOptionsModel,
  SelectedFieldModel,
  SelectedUserFields,
} from 'models/Enterprise/FieldsManagerModel';
import {
  stripInvalidFieldsFromUserFields,
  hasInvalidFiltersInUserFields,
  stripInvalidFiltersFromUserFields,
} from '../utils/validation/fieldValidation';

// --- Action Types ---

export type EnterpriseAction =
  | { type: 'SET_PARTITIONS'; partitions: PartitionModel[] }
  | { type: 'SET_ENTERPRISE_FIELDS'; fields: SelectedUserFields[] }
  | { type: 'UPDATE_USER_FIELDS'; fields: SelectedUserFields }
  | { type: 'SET_VIEW_OPTIONS'; options: FieldsManagerViewOptionsModel }
  | {
      type: 'UPDATE_FIELD_IN_PARTITION';
      partitionGuid: string;
      fieldIndex: number;
      updatedField: SelectedFieldModel;
    }
  | { type: 'SYNC_PARTITIONS_WITH_FIELDS'; partitions: PartitionModel[] }
  | { type: 'STRIP_INCOMPLETE_FIELDS'; partitionGuid: string }
  | { type: 'CLEAR_SELECTED_VALUES' }
  | { type: 'RESET'; state: EnterpriseGlobalModel };

/**
 * Removes incomplete fields and filters from a single partition entry.
 * WHY: computed here (inside the reducer) instead of by the caller so it always runs against the
 * freshest state. A read-compute-write from a component can be clobbered by an interleaved async
 * writer — which is what made stripped fields resurrect on tab-pop.
 * Edit-mode entries are left untouched, mirroring `getStrippedFieldsOnTabPop`.
 */
const stripIncompleteFromUserFields = (userFields: SelectedUserFields): SelectedUserFields => {
  if (userFields.fromEditMode) return userFields;

  const strippedFields = stripInvalidFieldsFromUserFields(userFields);
  const hasRemovedFields = strippedFields.fields.length !== userFields.fields.length;
  const hasRemovedFilters = hasInvalidFiltersInUserFields(strippedFields);

  if (!hasRemovedFields && !hasRemovedFilters) return userFields;

  return hasRemovedFilters ? stripInvalidFiltersFromUserFields(strippedFields) : strippedFields;
};

// --- Reducer ---

export const enterpriseReducer = (
  state: EnterpriseGlobalModel,
  action: EnterpriseAction
): EnterpriseGlobalModel => {
  switch (action.type) {
    case 'SET_PARTITIONS':
      return updateSelectedPartitions(state, action.partitions);

    case 'SET_ENTERPRISE_FIELDS':
      return updateSelectedEnterpriseFields(state, action.fields);

    case 'UPDATE_USER_FIELDS': {
      // Upsert by partitionGuid: replace the partition's entry if it exists, else append.
      // Keyed by partition (not array index) so it stays correct as partitions are added/removed.
      const { fields } = action;
      const existingIndex = state.selectedValues.selectedEnterpriseFields.findIndex(
        (f) => f.partitionGuid === fields.partitionGuid
      );

      if (existingIndex >= 0) {
        const updated = [...state.selectedValues.selectedEnterpriseFields];
        updated[existingIndex] = fields;
        return updateSelectedEnterpriseFields(state, updated);
      }

      return updateSelectedEnterpriseFields(state, [
        ...state.selectedValues.selectedEnterpriseFields,
        fields,
      ]);
    }

    case 'SET_VIEW_OPTIONS': {
      // Replace-by-partition: drop any prior options for this parent partition, then add the
      // new set. Avoids duplicate option groups when a partition's view options are reloaded.
      const existing = state.options.availableFieldsManagerOptions.filter(
        (opt) => opt.parentPartition?.value !== action.options.parentPartition?.value
      );
      return updateAvailableFieldsManagerOptions(state, [...existing, action.options]);
    }

    case 'UPDATE_FIELD_IN_PARTITION':
      return updateFieldInPartition(
        state,
        action.partitionGuid,
        action.fieldIndex,
        action.updatedField
      );

    case 'SYNC_PARTITIONS_WITH_FIELDS': {
      // Reconcile field entries to the active partition set: keep existing fields for partitions
      // that are still selected, seed empty entries for new ones, and drop entries for removed
      // partitions. Order follows the incoming partition order.
      const { partitions } = action;
      const activeGuids = partitions.map((p) => p.value);
      const existingByGuid = new Map(
        state.selectedValues.selectedEnterpriseFields.map((f) => [f.partitionGuid, f])
      );
      const synced: SelectedUserFields[] = activeGuids.map(
        (guid) => existingByGuid.get(guid) ?? { partitionGuid: guid, fields: [] }
      );
      const withPartitions = updateSelectedPartitions(state, partitions);
      return updateSelectedEnterpriseFields(withPartitions, synced);
    }

    case 'STRIP_INCOMPLETE_FIELDS': {
      const { partitionGuid } = action;
      let changed = false;
      const updated = state.selectedValues.selectedEnterpriseFields.map((uf) => {
        if (uf.partitionGuid !== partitionGuid) return uf;
        const stripped = stripIncompleteFromUserFields(uf);
        if (stripped !== uf) changed = true;
        return stripped;
      });
      return changed ? updateSelectedEnterpriseFields(state, updated) : state;
    }

    case 'CLEAR_SELECTED_VALUES':
      return {
        ...state,
        selectedValues: {
          selectedPartitions: [],
          selectedEnterpriseFields: [],
        },
      };

    case 'RESET':
      return action.state;

    default:
      return state;
  }
};

// --- Initial State ---

export const ENTERPRISE_INITIAL_STATE: EnterpriseGlobalModel = {
  options: {
    availableFieldsManagerOptions: [],
  },
  selectedValues: {
    selectedPartitions: [],
    selectedEnterpriseFields: [],
  },
};
